import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { BadgeCheck, Quote, Star } from "lucide-react";
import { Eyebrow, MaskText, Reveal } from "@/components/motion";
import { REVIEWS, REVIEW_COUNT, SHOP } from "@/data/catalog";
import { EASE } from "@/lib/scroll";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Stars = ({ n }) =>
  n ? (
    <span className="flex gap-0.5" aria-label={`${n} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`h-3.5 w-3.5 ${i <= n ? "fill-champagne text-champagne" : "text-stone/40"}`} />
      ))}
    </span>
  ) : null;

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "");

/** Live Judge.me reviews: cosmetics (pigments, lashes, blender, brushes) lead; the rest of the store follows, clearly labelled. */
export const Reviews = () => {
  const [live, setLive] = useState(null);
  useEffect(() => {
    axios
      .get(`${BACKEND}/api/reviews`)
      .then((r) => {
        if (r.data && !r.data.pending) setLive(r.data);
      })
      .catch(() => {});
  }, []);

  const { featured, cosmetics, store, total } = useMemo(() => {
    if (!live) {
      const [f, ...rest] = REVIEWS;
      return { featured: { ...f, product: "WINK lashes", rating: 5 }, cosmetics: [], store: rest.slice(0, 6), total: REVIEW_COUNT };
    }
    const cos = live.cosmetics.filter((r) => r.rating >= 4 && r.text.length > 8);
    const [f, ...restCos] = cos.length ? cos : live.store;
    return { featured: f, cosmetics: restCos, store: live.store.filter((r) => r.rating >= 4).slice(0, Math.max(0, 6 - restCos.length)), total: live.total };
  }, [live]);

  const Card = ({ r, i, cosmetic }) => (
    <motion.figure
      data-testid={`review-item-${i}`}
      data-cosmetic={cosmetic ? "true" : "false"}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, ease: EASE, delay: (i % 3) * 0.08 }}
      className="review-card group flex min-w-0 flex-col"
    >
      <div className="flex items-center justify-between gap-3">
        <Stars n={r.rating} />
        {r.verified && (
          <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-champagne"><BadgeCheck className="h-3.5 w-3.5" /> Verified buyer</span>
        )}
      </div>
      {r.title && <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-champagne">{r.title}</p>}
      <blockquote className="mt-3 flex-1 font-display text-2xl leading-snug text-cream">“{r.text}”</blockquote>
      <figcaption className="mt-8 border-t border-cream/10 pt-4 text-xs text-stone">
        <span className="flex items-center justify-between"><span className="text-cream/90">{r.name}</span><span className="font-mono">{r.date?.includes("-") ? fmtDate(r.date) : r.date}</span></span>
        {r.product && <span className="mt-1 block truncate">On {r.product}</span>}
      </figcaption>
    </motion.figure>
  );

  return (
    <section id="reviews" data-testid="reviews-section" className="theme-light relative bg-ink text-cream">
      <div className="mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-12 lg:py-36">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Eyebrow>08 — Let customers speak for us</Eyebrow>
            <p data-testid="reviews-count" className="mt-8 font-display text-[7rem] font-light leading-none text-champagne sm:text-[9rem]">{total}</p>
            <p className="mt-2 max-w-xs text-sm text-stone">
              verified reviews on Judge.me{live?.average ? ` · ${live.average} average` : ""}. Pulled live from the store, cosmetics first.
            </p>
          </div>
          <div className="lg:col-span-8">
            <Quote className="h-10 w-10 text-gold" />
            <h2 className="mt-6 font-display text-3xl font-light italic leading-[1.15] text-cream sm:text-4xl lg:text-5xl" data-testid="reviews-featured-quote">
              <MaskText lines={[`“${featured.text}”`]} />
            </h2>
            <p className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-xs uppercase tracking-[0.22em] text-stone" data-testid="reviews-featured-meta">
              <Stars n={featured.rating} />
              <span>{featured.name} · {featured.date?.includes("-") ? fmtDate(featured.date) : featured.date} · On {featured.product}</span>
            </p>
          </div>
        </div>

        {cosmetics.length > 0 && (
          <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="reviews-cosmetics">
            {cosmetics.map((r, i) => <Card key={r.id || i} r={r} i={i} cosmetic />)}
          </div>
        )}
        {store.length > 0 && (
          <>
            <p className="mt-14 font-mono text-xs uppercase tracking-[0.25em] text-stone" data-testid="reviews-store-label">More from Ace Vanity customers · across the store</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="reviews-store">
              {store.map((r, i) => <Card key={r.id || i} r={r} i={cosmetics.length + i} />)}
            </div>
          </>
        )}
        <Reveal delay={0.1}>
          <p className="mt-8 text-xs text-stone">
            Reviews are quoted as written by customers and refresh automatically from Judge.me.{" "}
            <a href={`${SHOP}/collections/celestial-pressed-pigment`} className="underline underline-offset-4 hover:text-champagne" data-testid="reviews-write-link">Bought a pigment, lash, blender or brush? Leave a review on its product page.</a>
          </p>
        </Reveal>
      </div>
    </section>
  );
};
